import { useCallback, useEffect, useRef } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string
          callback: (token: string) => void
          'error-callback'?: (code?: string) => void
          'expired-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
          refreshExpired?: 'auto' | 'manual'
        },
      ) => string
      remove: (id: string) => void
      reset: (id?: string) => void
    }
  }
}

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script'
const TURNSTILE_LOAD_EVENT = 'cf-turnstile-load'

let scriptLoadingPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('Turnstile script failed')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.id = TURNSTILE_SCRIPT_ID
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true
    script.onload = () => {
      window.dispatchEvent(new Event(TURNSTILE_LOAD_EVENT))
      resolve()
    }
    script.onerror = () => reject(new Error('Turnstile script failed'))
    document.head.appendChild(script)
  })

  return scriptLoadingPromise
}

interface Props {
  siteKey: string
  onVerify: (token: string) => void
  onError?: (code?: string) => void
  theme?: 'light' | 'dark' | 'auto'
  /** Change de valeur = nouveau test, nouveau jeton. Un jeton ne sert qu'une fois :
   *  le serveur le marque utilisé dès qu'il l'a vérifié, même si l'envoi est refusé. */
  reinitialiser?: number
}

export function TurnstileWidget({ siteKey, onVerify, onError, theme = 'auto', reinitialiser = 0 }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)

  // Refs stables pour callbacks (évite re-render)
  const onVerifyRef = useRef(onVerify)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onVerifyRef.current = onVerify
    onErrorRef.current = onError
  })

  const mount = useCallback(() => {
    if (!containerRef.current || !window.turnstile) return
    if (widgetIdRef.current) return // déjà rendu
    if (!siteKey) {
      console.error('[Turnstile] siteKey manquant')
      return
    }

    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onVerifyRef.current(token),
        'error-callback': (code) => {
          console.error('[Turnstile] error:', code)
          onVerifyRef.current('')
          onErrorRef.current?.(code)
        },
        'expired-callback': () => onVerifyRef.current(''),
        refreshExpired: 'auto',
        theme,
      })
    } catch (err) {
      console.error('[Turnstile] render failed:', err)
    }
  }, [siteKey, theme])

  useEffect(() => {
    let cancelled = false
    const handler = () => {
      if (!cancelled) mount()
    }

    void loadTurnstileScript().then(() => {
      if (!cancelled) mount()
    })
    window.addEventListener(TURNSTILE_LOAD_EVENT, handler)

    return () => {
      cancelled = true
      window.removeEventListener(TURNSTILE_LOAD_EVENT, handler)
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // ignore
        }
        widgetIdRef.current = null
      }
    }
  }, [mount])

  useEffect(() => {
    if (!reinitialiser || !widgetIdRef.current || !window.turnstile) return
    try {
      window.turnstile.reset(widgetIdRef.current)
    } catch (err) {
      console.error('[Turnstile] reset failed:', err)
    }
  }, [reinitialiser])

  return <div ref={containerRef} className="cf-turnstile" />
}
